import ComptoirShell from '@/components/comptoir/ComptoirShell'
import CustomerCreationModal from '@/components/comptoir/CustomerCreationModal'
import CustomerPicker from '@/components/comptoir/CustomerPicker'
import EmptyComptoirState from '@/components/comptoir/EmptyComptoirState'
import OrdersScopeToggle from '@/components/comptoir/OrdersScopeToggle'
import CourierOrderSummaryCard from '@/components/orders/CourierOrderSummaryCard'
import OrderCard from '@/components/orders/OrderCard'
import { useCustomers } from '@/queries/customers'
import { buildCatalogFor, createDraftOrder } from '@/utils/orders'
import { ArticleService, Contract, Customer, Order, OrdersScope, Printer, SharedProps } from '@/types'
import { usePage } from '@inertiajs/react'
import { Badge, Button, Collapse, Group, Select, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { useEffect, useState } from 'react'

function ComptoirAtelierPage({
  orders,
  contracts,
  article_services,
  printers,
  mode,
}: {
  orders: Order[]
  contracts: Contract[]
  article_services: ArticleService[]
  printers: Printer[]
  mode: 'particulier' | 'societe' | null
}) {
  const { current_organization, current_user } = usePage<SharedProps>().props
  const isSocieteMode = mode === 'societe'
  const organizationSlug = current_organization!.slug

  const [opened, { open, close }] = useDisclosure(false)
  const [customerCreationOpened, customerCreation] = useDisclosure(false)
  const [customerSearch, setCustomerSearch] = useState('')
  const [debouncedCustomerSearch] = useDebouncedValue(customerSearch, 200)
  const [contractId, setContractId] = useState<number | null>(null)
  const [activeCourierOrderId, setActiveCourierOrderId] = useState<number | null>(null)
  const [showCourierSection, setShowCourierSection] = useState(false)
  const [newlyCreatedOrderId, setNewlyCreatedOrderId] = useState<number | null>(null)
  const [ordersScope, setOrdersScope] = useState<OrdersScope>('mine')

  const { data: customers = [], isFetching: customersLoading } = useCustomers({
    organizationSlug,
    query: debouncedCustomerSearch,
  })

  const courierOrders = orders.filter((order) => order.creator?.role === 'courier')
  const comptoirOrders = orders.filter((order) => order.creator?.role !== 'courier')
  const myComptoirOrders = comptoirOrders.filter((order) => order.created_by_id === current_user?.id)
  const visibleComptoirOrders = ordersScope === 'mine' ? myComptoirOrders : comptoirOrders

  const catalogFor = buildCatalogFor(contracts, article_services)

  const submitOrder = (customerId: number | null) =>
    createDraftOrder({
      organizationSlug,
      customerId,
      contractId: isSocieteMode ? contractId : null,
      onSuccess: (orderId) => {
        close()
        if (orderId) setNewlyCreatedOrderId(orderId)
      },
    })

  const handleCustomerCreated = (customer: Customer) => {
    customerCreation.close()
    submitOrder(customer.id)
  }

  useEffect(() => {
    if (!opened) {
      setContractId(null)
      setCustomerSearch('')
    }
  }, [opened])

  const modalChildren = isSocieteMode ? (
    <>
      <Select
        label="Contrat"
        placeholder="Choisir un contrat"
        data={contracts.map((contract) => ({ value: String(contract.id), label: contract.customer_name }))}
        value={contractId ? String(contractId) : null}
        onChange={(value) => setContractId(value ? Number(value) : null)}
        searchable
        required
      />
      <Button disabled={!contractId} onClick={() => submitOrder(null)}>
        Créer la commande
      </Button>
    </>
  ) : (
    <CustomerPicker
      customers={customers}
      searchValue={customerSearch}
      onSearchChange={setCustomerSearch}
      onCustomerSelect={(customer) => submitOrder(customer.id)}
      onNewCustomerClick={customerCreation.open}
      isLoading={customersLoading}
    />
  )

  return (
    <>
      <ComptoirShell title="Comptoir" opened={opened} onOpen={open} onClose={close} modalChildren={modalChildren}>
        {orders.length === 0 && <EmptyComptoirState />}

        {orders.length > 0 && (
          <Stack gap="xl">
            {courierOrders.length > 0 && (
              <Stack gap="sm">
                <UnstyledButton onClick={() => setShowCourierSection(!showCourierSection)}>
                  <Group>
                    <Title order={3}>Livraisons coursier</Title>
                    <Badge color="blue" variant="light">
                      {courierOrders.length}
                    </Badge>
                    {showCourierSection ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                  </Group>
                </UnstyledButton>

                <Collapse in={showCourierSection}>
                  <Stack>
                    {courierOrders.map((order: Order) =>
                      activeCourierOrderId === order.id ? (
                        <OrderCard
                          key={order.id}
                          order={order}
                          organizationSlug={organizationSlug}
                          articleServices={catalogFor(order)}
                          autoOpen={order.id === newlyCreatedOrderId}
                        />
                      ) : (
                        <CourierOrderSummaryCard
                          key={order.id}
                          order={order}
                          onClick={() => setActiveCourierOrderId(order.id)}
                        />
                      )
                    )}
                  </Stack>
                </Collapse>
              </Stack>
            )}

            {comptoirOrders.length > 0 && (
              <Stack>
                {courierOrders.length > 0 && <Title order={3}>Commandes en cours</Title>}
                <OrdersScopeToggle
                  value={ordersScope}
                  onChange={setOrdersScope}
                  mineCount={myComptoirOrders.length}
                  allCount={comptoirOrders.length}
                />
                {visibleComptoirOrders.length === 0 ? (
                  <Text c="dimmed" size="sm">
                    Vous n'avez aucune commande en cours. Affichez « Toutes » pour voir celles de l'équipe.
                  </Text>
                ) : (
                  visibleComptoirOrders.map((order: Order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      organizationSlug={organizationSlug}
                      articleServices={catalogFor(order)}
                      printers={printers}
                      autoOpen={order.id === newlyCreatedOrderId}
                    />
                  ))
                )}
              </Stack>
            )}
          </Stack>
        )}
      </ComptoirShell>

      <CustomerCreationModal
        opened={customerCreationOpened}
        onClose={customerCreation.close}
        onCreated={handleCustomerCreated}
        organizationSlug={organizationSlug}
      />
    </>
  )
}

export default ComptoirAtelierPage
